// Sugalarm — activation (delivery-readiness) reporting, cycle 365.
//
// WHY THIS EXISTS. A phone can be signed in, streaming readings, and
// painting a glance tile while being structurally unable to receive an
// alarm. That is not hypothetical: a real household iPhone held iOS
// Live-Activity tokens (so it was plainly running this shell) with NO APNs
// token row — Live Activities need no notification permission, alarm push
// does. Nobody could see it, because the failure was an ABSENCE.
//
// The mechanism behind that absence is right here in the SPA:
// `bootstrapNativePush` returns early unless the local
// `sugalarm.nativePush.enabled` flag is set, and on a phone that flag is
// only set by tapping "Enable on this device" in Settings → Notifications.
// Skip the cycle-316 tour once and the phone never registers a token —
// silently, forever.
//
// THE VERDICT IS THE SERVER'S. This module collects facts and posts them;
// `activation_service.evaluate` decides. Deliberate: the same evaluator
// feeds the in-app prompt and the admin roster, so they cannot drift. The
// only rule implemented locally is the presentation gate at the bottom.

import * as alarmReliability from "./alarmReliability";
import { capacitorPlatform, isCapacitorNative } from "./capacitor";
import { takeClientEvents, type ClientEvent } from "./clientEvents";
import * as notificationSettings from "./notificationSettings";
import { transportNote } from "./ws";

/** Cycle 429 — the native shell's own build, read through the bridge-
 *  injected `App` plugin (already on the bridge: it is the plugin the
 *  wake handler listens to). `shell_version` has existed on the activation
 *  row since cycle 365 and had been NULL on every row the shells ever
 *  posted, so the fleet's native builds could not be read from the
 *  database. Web sessions post `"web"`. Never throws; a bridge without the
 *  method yields null (unknown), never a guess. Bounded to the column's 32
 *  characters. */
export async function readShellVersion(): Promise<string | null> {
  if (!isCapacitorNative()) return "web";
  try {
    const cap = (
      window as {
        Capacitor?: {
          Plugins?: {
            App?: { getInfo?: () => Promise<{ version?: string; build?: string }> };
          };
        };
      }
    ).Capacitor;
    const getInfo = cap?.Plugins?.App?.getInfo;
    if (typeof getInfo !== "function") return null;
    const info = await getInfo();
    const version = (info?.version ?? "").trim();
    const build = (info?.build ?? "").trim();
    if (!version && !build) return null;
    const label = build && build !== version ? `${version}+${build}` : version || build;
    return label.slice(0, 32);
  } catch {
    return null;
  }
}

/** Stable wire values — mirrors `server/schemas/activation.py`. */
export type ActivationGap =
  | "notifications_denied"
  | "no_push_token"
  | "token_claim_without_row"
  | "critical_alerts_off"
  | "live_activities_off"
  | "no_widget";

export type ActivationSeverity = "critical" | "advisory" | "ok";

export interface ActivationVerdict {
  severity: ActivationSeverity;
  gaps: ActivationGap[];
  has_push_token: boolean;
  /** Cycle 431: how the posted client-claimed events were handled. */
  client_events?: Record<string, number> | null;
}

export interface ActivationReport {
  client_id: string;
  platform: "ios" | "android" | "web";
  app_version?: string | null;
  shell_version?: string | null;
  notifications_enabled?: boolean | null;
  critical_alerts_enabled?: boolean | null;
  live_activities_enabled?: boolean | null;
  activity_running?: boolean | null;
  widget_installed?: boolean | null;
  push_token_claimed?: boolean | null;
  note?: string | null;
  /** Cycle 431: client-claimed transport events since the last report
   *  (at most 20; the server keys its cap on the identity, not on
   *  `client_id`). */
  events?: ClientEvent[];
}

/** Set when a token registration POST actually succeeded on this install.
 *  Compared server-side against the real token rows: a claim with no row
 *  is a registration that failed silently, which is its own gap. */
const TOKEN_CLAIM_KEY = "sugalarm.pushTokenRegistered";

export function markPushTokenRegistered(): void {
  try {
    localStorage.setItem(TOKEN_CLAIM_KEY, "1");
  } catch {
    /* private mode / storage disabled — the report just says unknown */
  }
}

export function clearPushTokenRegistered(): void {
  try {
    localStorage.removeItem(TOKEN_CLAIM_KEY);
  } catch {
    /* ignore */
  }
}

function pushTokenClaimed(): boolean | null {
  try {
    return localStorage.getItem(TOKEN_CLAIM_KEY) === "1";
  } catch {
    return null;
  }
}

export function reportPlatform(): "ios" | "android" | "web" {
  if (!isCapacitorNative()) return "web";
  const p = capacitorPlatform();
  if (p === "ios") return "ios";
  if (p === "android") return "android";
  return "web";
}

/** Collect this install's capability facts.
 *
 *  Tri-state discipline: a field is only `false` when the OS said so.
 *  Anything this surface cannot determine stays `null` — the server
 *  treats null as "unknown" and never as a gap, so we never nag someone
 *  who is correctly set up.
 */
export async function collectActivationReport(
  clientId: string,
  appVersion: string,
): Promise<ActivationReport> {
  const platform = reportPlatform();
  const report: ActivationReport = {
    client_id: clientId,
    platform,
    app_version: appVersion,
    // Cycle 429: the native build (or "web"), and this page load's
    // transport counters as the note — counts only, never a value.
    shell_version: await readShellVersion(),
    note: transportNote().slice(0, 200),
    push_token_claimed: pushTokenClaimed(),
  };
  // Cycle 431: the transport claims since the last report, drained here.
  const events = takeClientEvents();
  if (events.length > 0) report.events = events;

  if (platform === "ios") {
    const s = await notificationSettings.getStatus();
    if (s) {
      report.notifications_enabled = s.authorization === "authorized";
      report.critical_alerts_enabled =
        s.criticalAlerts === "notSupported" ? null : s.criticalAlerts === "enabled";
    }
    const glance = await notificationSettings.getGlanceStatus();
    if (glance) {
      report.live_activities_enabled = glance.liveActivitiesEnabled;
      report.activity_running = glance.activityRunning;
      report.widget_installed = glance.widgetInstalled;
    }
  } else if (platform === "android") {
    const s = await alarmReliability.getStatus();
    if (s && s.supported) {
      report.notifications_enabled = s.notificationsEnabled ?? null;
    }
  }

  return report;
}

/** True when the OS has already granted notifications, so registering a
 *  push token needs NO user interaction and NO prompt — the silent
 *  self-heal path for an install whose local opt-in flag was never set
 *  (or was cleared by a webview data wipe). */
export function canSilentlyArm(report: ActivationReport): boolean {
  return report.platform !== "web" && report.notifications_enabled === true;
}

/** Presentation gate for the prompt — pure, unit-tested.
 *
 *  - Never over a live alarm: an alarm surface and its Snooze/Stop always
 *    win the screen (the cycle-81 control floor).
 *  - `critical` is a safety gap, so a "not now" only holds for the
 *    session. This is intentionally NOT once-per-install like the
 *    cycle-316 tour: skipping that tour once is exactly how a phone ends
 *    up permanently unable to be paged.
 *  - `advisory` (glance-only) is gentler: dismiss holds persistently.
 */
export function shouldShowActivationPrompt(input: {
  verdict: ActivationVerdict | null;
  alarmLive: boolean;
  dismissedSessionSeverity: ActivationSeverity | null;
  advisoryOptedOut: boolean;
}): boolean {
  const { verdict, alarmLive, dismissedSessionSeverity, advisoryOptedOut } = input;
  if (!verdict) return false;
  if (alarmLive) return false;
  if (verdict.severity === "ok") return false;
  if (verdict.severity === "advisory") {
    if (advisoryOptedOut) return false;
    return dismissedSessionSeverity !== "advisory";
  }
  return dismissedSessionSeverity !== "critical";
}

/** Human copy for a gap — the WHY, not the mechanism. Kept here so the
 *  prompt and any future admin/Settings surface read identically. */
export function gapCopy(gap: ActivationGap): string {
  switch (gap) {
    case "notifications_denied":
      return "Notifications are turned off, so alarms can't reach this phone.";
    case "no_push_token":
      return "This phone isn't registered for alarms yet.";
    case "token_claim_without_row":
      return "Alarm registration didn't complete — tap to finish it.";
    case "critical_alerts_off":
      return "Critical Alerts are off, so a silenced phone stays silent at night.";
    case "live_activities_off":
      return "Live Activities are off, so the glucose tile can't appear.";
    case "no_widget":
      return "No glucose widget is on your Home or Lock Screen yet.";
    default:
      return "Alert delivery needs attention.";
  }
}

/** What the prompt's primary button can actually DO about a gap.
 *
 * Cycle 365.2 — this exists because the first version branched on
 * SEVERITY, and a `no_widget` card therefore offered "Turn on alerts",
 * which called `enableNativePush()`, found permission already granted,
 * returned instantly, changed nothing, and re-rendered the identical
 * card. A dead button on a safety product is worse than no button: it
 * teaches people the prompt is broken and trains them to dismiss it.
 *
 * The rule: pick the action from the GAP, and when nothing in-app can
 * fix it, do not render an action at all.
 */
export type ActivationAction = "enable_push" | "open_settings" | "instructions";

export function primaryActionFor(gaps: ActivationGap[]): ActivationAction {
  // Denied permission first: re-requesting in-app is a silent no-op once
  // the user has said no, so Settings is the ONLY recovery path.
  if (gaps.includes("notifications_denied")) return "open_settings";
  // Permission is fine but no token landed — this one the app CAN fix.
  if (gaps.includes("no_push_token") || gaps.includes("token_claim_without_row")) {
    return "enable_push";
  }
  // Toggles that live in iOS Settings, not in our app.
  if (gaps.includes("critical_alerts_off") || gaps.includes("live_activities_off")) {
    return "open_settings";
  }
  // Adding a widget is a manual Home/Lock Screen gesture. There is NO iOS
  // API for it, so the honest affordance is telling someone how.
  return "instructions";
}

export function primaryActionLabel(action: ActivationAction): string {
  switch (action) {
    case "enable_push":
      return "Turn on alerts";
    case "open_settings":
      return "Open Settings";
    default:
      return "Got it";
  }
}

/** Steps for the one gap the app cannot resolve for you. */
export const WIDGET_STEPS: string[] = [
  "Lock Screen: touch and hold it → Customize → tap a widget slot → Sugar Alarm.",
  "Home Screen: touch and hold → tap + → search Sugar Alarm.",
];
